import PageLayout from '@/components/layout/PageLayout';

export default function TermsPage() {
  return (
    <PageLayout>
      <div className="section-padding bg-white dark:bg-secondary-900">
        <div className="container-custom max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold text-secondary-900 dark:text-white mb-8">
            Terms of Service
          </h1>

          <div className="prose prose-lg dark:prose-invert max-w-none">
            <p className="text-lg text-secondary-600 dark:text-secondary-400 mb-8">
              Last updated: 11 March 2026
            </p>

            <p className="text-secondary-700 dark:text-secondary-300 mb-12 text-lg leading-relaxed">
              These Terms of Service ("Terms") govern your use of Notematic, a clinical documentation assistance platform.
              By accessing or using Notematic, you agree to be bound by these Terms and all applicable laws and regulations.
            </p>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                1. Acceptance of Terms
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300">
                By accessing or using Notematic, you agree to be bound by these Terms of Service
                and all applicable laws and regulations, including the Privacy Act 1988 (Cth) and
                Australian Privacy Principles (APPs).
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                2. System of Record and Data Processing
              </h2>

              <div className="bg-primary-50 dark:bg-primary-950 border-l-4 border-primary-600 p-6 mb-6 rounded-r-lg">
                <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                  <strong>IMPORTANT: Notematic is a Processing Tool, Not a Record-Keeping System</strong>
                </p>
                <p className="text-secondary-700 dark:text-secondary-300">
                  Notematic is a clinical documentation assistance and data processing platform. It is NOT a clinical
                  record-keeping system and does NOT function as the system of record for patient health information.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                System of Record
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li><strong>Your clinic's EMR/CRM system is the authoritative repository</strong> for all clinical records.</li>
                <li><strong>Your clinic's EMR/CRM is solely responsible</strong> for statutory health record retention obligations (typically 7+ years, or longer for minors).</li>
                <li><strong>All clinical documentation generated by Notematic must be exported</strong> to your clinic's primary system prior to deletion from Notematic.</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Processing Role and Temporary Data Lifecycle
              </h3>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                Notematic temporarily processes audio recordings, transcripts, and draft notes solely to:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li>Generate a templated draft clinical note using AI assistance</li>
                <li>Support clinician review, editing, and approval</li>
                <li>Export the finalized note to your clinic's EMR/CRM system</li>
              </ul>
              <p className="text-secondary-700 dark:text-secondary-300 mb-6">
                Notematic retains <strong>temporary working copies only</strong> for operational purposes during this processing lifecycle.
                After export (and after short grace windows for verification and recovery), Notematic <strong>deletes transcripts
                and drafts</strong> in accordance with our data retention policy. Audio is never stored — it is processed transiently in-memory only and discarded immediately after transcription.
              </p>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Export Requirement
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li><strong>All approved clinical notes must be exported</strong> to your clinic's system of record.</li>
                <li><strong>You are responsible for verifying successful export</strong> and proper storage in your EMR/CRM.</li>
                <li><strong>Notematic will delete drafts</strong> within 24 hours after export.</li>
                <li><strong>You must ensure your EMR/CRM complies</strong> with statutory retention obligations in your jurisdiction.</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                3. Service Description
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                Notematic provides AI-assisted clinical documentation services for mental health
                practitioners, including:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li>Session recording with explicit consent</li>
                <li>AI-powered transcription using Microsoft Azure Speech Service</li>
                <li>Draft note generation using Microsoft Azure OpenAI Service</li>
                <li>Clinician review, editing, and approval workflow</li>
                <li>Export to your clinic's EMR/CRM system</li>
                <li>Secure temporary storage during processing</li>
                <li>Automated data retention and deletion</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                4. User Responsibilities
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                As a user of Notematic, you agree to:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li>Maintain the confidentiality of your account credentials</li>
                <li>Be responsible for all activities under your account</li>
                <li>Obtain explicit consent from all parties before recording sessions</li>
                <li>Review and verify all AI-generated content before finalization</li>
                <li>Export all approved clinical notes to your clinic's EMR/CRM system</li>
                <li>Verify successful export and proper storage in your system of record</li>
                <li>Comply with all applicable professional standards and regulations</li>
                <li>Not use the service for any unlawful or unauthorized purpose</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                5. Professional Responsibility and AI-Generated Content
              </h2>

              <div className="bg-amber-50 dark:bg-amber-950 border-l-4 border-amber-600 p-6 mb-6 rounded-r-lg">
                <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                  <strong>CRITICAL: AI-Generated Content is Draft-Only</strong>
                </p>
                <p className="text-secondary-700 dark:text-secondary-300">
                  All AI-generated content is provided as <strong>draft assistance only</strong> and must be reviewed,
                  verified, and approved by qualified mental health practitioners before finalization.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Clinician Responsibility
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li><strong>You remain fully responsible</strong> for all clinical decisions, assessments, and documentation.</li>
                <li><strong>You must review and verify</strong> all AI-generated content for accuracy, completeness, and clinical appropriateness.</li>
                <li><strong>You must not rely solely on AI outputs</strong> for diagnosis, treatment planning, or risk assessment.</li>
                <li><strong>You are responsible for identifying and correcting</strong> any errors, omissions, or inappropriate content in AI-generated drafts.</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                No Autonomous Clinical Actions
              </h3>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                Notematic does NOT and will NOT:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li>Make clinical diagnoses</li>
                <li>Prescribe treatment or interventions</li>
                <li>Classify risk levels without human review</li>
                <li>Make legal or clinical determinations</li>
                <li>Auto-submit content to permanent clinical records</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                High-Risk Indicators
              </h3>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                For high-risk clinical indicators (suicidality, abuse, psychosis, imminent harm):
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li>Content is flagged for immediate clinician review</li>
                <li>No automated interpretation or recommendations are provided</li>
                <li>Clinician must assess and respond according to professional standards</li>
                <li>AI outputs must not downplay or normalize high-risk signals</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                6. Data Retention and Deletion
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                Notematic implements a comprehensive data retention and deletion policy. For full details, please refer to our Privacy Policy.
              </p>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Retention Periods
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li><strong>Audio Recordings:</strong> Not retained — processed transiently in-memory only and discarded immediately after transcription</li>
                <li><strong>Transcripts:</strong> Until review, maximum 30 days post-finalization</li>
                <li><strong>Draft Notes:</strong> Deleted within 24 hours after export to your EMR/CRM</li>
                <li><strong>Client Session Consent Records:</strong> 7 years from the date of the session (Health Records Act 2001)</li>
                <li><strong>Clinician Organisational Consent Records:</strong> Duration of active clinician account + 7 years after account closure (Health Records Act 2001) — non-withdrawable</li>
                <li><strong>Audit Logs:</strong> 7 years (no clinical content)</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Two-Stage Deletion Process
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li><strong>Soft-Delete:</strong> Immediate hiding from normal queries with 30-day recovery period</li>
                <li><strong>Hard-Delete:</strong> Permanent and irreversible deletion from all storage layers after 30 days</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                7. Clinician Responsibilities
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                As a mental health practitioner using Notematic, you are responsible for:
              </p>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                System of Record Maintenance
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li><strong>Maintaining a proper EMR/CRM system</strong> as your authoritative clinical record repository</li>
                <li><strong>Ensuring your EMR/CRM complies</strong> with statutory health record retention obligations (typically 7+ years, or longer for minors)</li>
                <li><strong>Configuring appropriate retention periods</strong> in your EMR/CRM for different client populations (adults, minors, etc.)</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Export and Verification
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li><strong>Exporting all approved clinical notes</strong> from Notematic to your EMR/CRM before deletion</li>
                <li><strong>Verifying successful export</strong> and proper storage in your system of record</li>
                <li><strong>Understanding that Notematic will delete drafts</strong> within 24 hours after export</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Organisational Consent Obligation
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li><strong>Completing the mandatory onboarding consent</strong> — clinicians must read and explicitly accept all 12 obligations before using Notematic. This one-time organisational consent is non-withdrawable and retained for 7 years after account closure.</li>
                <li><strong>Obtaining all-party session consent</strong> from every session participant before using recording or transcription features, in accordance with applicable state legislation.</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Statutory Compliance
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li><strong>Complying with all applicable professional standards</strong> and regulations in your jurisdiction</li>
                <li><strong>Meeting statutory retention obligations</strong> for health records (Health Records Act 2001 and equivalent state/territory legislation)</li>
                <li><strong>Ensuring proper consent</strong> is obtained from all parties before recording sessions</li>
                <li><strong>Protecting client confidentiality</strong> and privacy in accordance with professional ethics</li>
                <li><strong>Notifying clients of data correction outcomes</strong> — when a client's personal information has been corrected following a request, you are responsible for informing the client of the outcome directly. Notematic does not do this on behalf of clinicians.</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                8. Limitation of Liability
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300">
                To the maximum extent permitted by law, Notematic shall not be liable for any
                indirect, incidental, special, consequential, or punitive damages arising from your
                use of the service. You acknowledge that Notematic is a processing tool and does not
                assume responsibility for clinical outcomes, professional liability, or statutory record retention obligations.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                9. Termination
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                We reserve the right to suspend or terminate your access to Notematic at any time for:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li>Violation of these Terms of Service</li>
                <li>Fraudulent or unlawful use of the service</li>
                <li>Non-payment of fees (if applicable)</li>
                <li>Any conduct that we determine, in our sole discretion, to be harmful to other users or the service</li>
              </ul>
              <p className="text-secondary-700 dark:text-secondary-300 mt-6">
                Upon termination, you must immediately export all clinical notes to your EMR/CRM system. Notematic will retain data for 30 days after termination to allow for export, after which all data will be permanently deleted.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                10. Governing Law
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300">
                These Terms of Service are governed by the laws of the Australian Capital Territory and the Commonwealth of Australia.
                Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of the Australian Capital Territory.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                11. Changes to Terms
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300">
                We may update these Terms of Service from time to time to reflect changes in technology, law, or our practices.
                The updated version will be published on our website and app with the "last updated" date. Continued use of the service
                after changes constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                12. Contact Information
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-6">
                For questions about these Terms of Service, please contact:
              </p>
              <div className="bg-secondary-50 dark:bg-secondary-800 p-6 rounded-lg border border-secondary-200 dark:border-secondary-700">
                <p className="text-secondary-700 dark:text-secondary-300 mb-3">
                  <strong className="text-secondary-900 dark:text-white">📧 Email:</strong> legal@notematic.com.au
                </p>
                <p className="text-secondary-700 dark:text-secondary-300">
                  <strong className="text-secondary-900 dark:text-white">📍 Address:</strong> Notematic Pty Ltd, 231 La Perouse street Red Hill ACT 2603
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

